
import { StudentEvent } from "../types";

export const processScheduledEvents = (
  scheduledEvents: any[] | null, 
  studentId: string,
  language: string
): StudentEvent[] => {
  if (!scheduledEvents) return [];
  
  console.log('=== PROCESSING SCHEDULED EVENTS ===');
  console.log('Total scheduled events:', scheduledEvents.length);
  console.log('Current student ID:', studentId);
  
  return scheduledEvents.map(event => {
    // Determine if this event belongs to the current student
    const isCurrentStudent = event.student_id === studentId;
    
    console.log(`Event ${event.id}: student_id=${event.student_id}, isCurrentStudent=${isCurrentStudent}`);
    
    // For events that belong to the current student, show all details
    if (isCurrentStudent) {
      return {
        ...event,
        isCurrentStudent: true,
        language
      };
    }
    
    // For events that don't belong to the current student,
    // only show them as "Occupied" slots with limited info
    return {
      ...event,
      title: language === 'pt' ? "Ocupado" : "Occupied",
      description: "",
      isCurrentStudent: false,
      language
    };
  });
};

export const processScheduleRequests = (
  requests: any[] | null, 
  studentId: string,
  language: string,
  existingEvents: StudentEvent[]
): StudentEvent[] => {
  console.log('=== PROCESSING SCHEDULE REQUESTS ===');
  console.log('Total requests received:', requests?.length || 0);
  console.log('Current student ID:', studentId);
  console.log('Language:', language);
  
  if (!requests) {
    console.log('No requests to process');
    return [];
  }

  console.log('Raw requests:', requests.map(r => ({
    id: r.id,
    student_id: r.student_id,
    status: r.status,
    requested_date: r.requested_date
  })));
  
  // Filter out requests that are already in the events list (only for other students' approved requests)
  const filteredRequests = requests.filter(request => {
    // Convert request.requested_date to a date string for comparison
    const requestDate = new Date(request.requested_date).toISOString();
    
    // For the current student's approved requests, keep them visible until they're properly handled
    const isCurrentStudent = request.student_id === studentId;
    if (isCurrentStudent && request.status === 'approved') {
      console.log(`Keeping approved request ${request.id} for current student`);
      return true;
    }
    
    // Check if there's an existing event with the same date and student
    const isDuplicate = existingEvents.some(event => 
      event.start_time === requestDate && 
      event.student_id === request.student_id
    );
    
    console.log(`Request ${request.id}: isDuplicate=${isDuplicate}, date=${requestDate}, status=${request.status}`);
    
    return !isDuplicate;
  });
  
  console.log('Filtered requests count:', filteredRequests.length);
  
  return filteredRequests.map(request => {
    // Calculate end time (1 hour from requested time)
    const startTime = new Date(request.requested_date);
    const endTime = new Date(startTime.getTime() + (60 * 60 * 1000));
    
    // Determine if this request belongs to the current student
    const isCurrentStudent = request.student_id === studentId;
    
    console.log(`Processing request ${request.id}:`);
    console.log(`  - student_id: ${request.student_id}`);
    console.log(`  - current student: ${studentId}`);
    console.log(`  - isCurrentStudent: ${isCurrentStudent}`);
    console.log(`  - status: ${request.status}`);
    console.log(`  - requested_date: ${request.requested_date}`);
    
    // For pending requests from other students, show as occupied
    if (!isCurrentStudent && request.status === 'pending') {
      console.log(`  -> Creating OCCUPIED event for other student`);
      return {
        id: request.id,
        title: language === 'pt' ? "Ocupado" : 
               language === 'es' ? "Ocupado" :
               language === 'fr' ? "Occupé" : 
               "Occupied",
        start_time: request.requested_date,
        end_time: endTime.toISOString(),
        status: request.status,
        student_id: request.student_id,
        trainer_id: request.trainer_id,
        type: 'occupied',
        isCurrentStudent: false,
        is_online: request.request_online || false,
        language
      };
    }
    
    // For requests from the current student
    if (isCurrentStudent) {
      console.log(`  -> Creating event for current student with status: ${request.status}`);
      
      // If approved, show as "AULA" (scheduled class)
      if (request.status === 'approved') {
        return {
          id: request.id,
          title: language === 'pt' ? "AULA" : 
                 language === 'es' ? "CLASE" :
                 language === 'fr' ? "COURS" :
                 "CLASS",
          start_time: request.requested_date,
          end_time: endTime.toISOString(),
          status: 'scheduled', // Change status to scheduled for approved requests
          student_id: request.student_id,
          trainer_id: request.trainer_id,
          type: 'approved_request',
          isCurrentStudent,
          is_online: request.request_online || false,
          language
        };
      }
      
      // If pending, show as request
      return {
        id: request.id,
        title: request.request_online ? 
          (language === 'pt' ? "Pedido de Aula Online" : 
           language === 'es' ? "Solicitud de Clase Online" :
           language === 'fr' ? "Demande de Cours En Ligne" :
           "Online Class Request") :
          (language === 'pt' ? "Pedido de Aula" : 
           language === 'es' ? "Solicitud de Clase" :
           language === 'fr' ? "Demande de Cours" :
           "Class Request"),
        start_time: request.requested_date,
        end_time: endTime.toISOString(),
        status: request.status,
        student_id: request.student_id,
        trainer_id: request.trainer_id,
        type: 'request',
        isCurrentStudent,
        is_online: request.request_online || false,
        language
      };
    }
    
    // For approved/rejected requests from other students, don't show them
    console.log(`  -> Skipping non-pending request from other student (status: ${request.status})`);
    return null;
  }).filter(Boolean) as StudentEvent[];
};

export const sortEventsByDate = (events: StudentEvent[]): StudentEvent[] => {
  const sorted = [...events].sort((a, b) => {
    return new Date(a.start_time).getTime() - new Date(b.start_time).getTime();
  });
  
  console.log('=== FINAL SORTED EVENTS ===');
  console.log('Total events after sorting:', sorted.length);
  sorted.forEach(event => {
    console.log(`Event: ${event.title} | Student: ${event.student_id} | Status: ${event.status} | Type: ${event.type} | Current: ${event.isCurrentStudent}`);
  });
  
  return sorted;
};
